import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  CompositionSchema,
  CompositionCreateInputSchema,
  CompositionUpdateInputSchema,
  type Composition,
  type CompositionCreateInput,
  type CompositionUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `composition` (recette / nomenclature, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`CompositionCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { CompositionCreateInput };

/** Nom de la collection Firestore de la famille composition. */
export const COMPOSITIONS_COLLECTION = 'compositions';

/**
 * Repository typé pour la famille `composition`.
 * Toute lecture est validée par `CompositionSchema`, toute écriture par
 * `CompositionCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface CompositionRepository {
  create(input: CompositionCreateInput): Promise<Composition>;
  list(options?: ListOptions): Promise<Composition[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Composition | null>;
  update(id: string, patch: CompositionUpdateInput): Promise<Composition>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Composition>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Composition>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Composition>;
}

const compositionConverter = createConverter<Composition>((raw: DocumentData) => CompositionSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createCompositionRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): CompositionRepository {
  const col = collection(db, COMPOSITIONS_COLLECTION).withConverter(compositionConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Composition>(db, col, COMPOSITIONS_COLLECTION, getActorId);

  return {
    async create(input: CompositionCreateInput): Promise<Composition> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = CompositionCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.components !== undefined) optional['components'] = fields.components;
      if (fields.steps !== undefined) optional['steps'] = fields.steps;
      if (fields.yield !== undefined) optional['yield'] = fields.yield;
      if (fields.computedCost !== undefined) optional['computedCost'] = fields.computedCost;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        productId: fields.productId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Composition);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return CompositionSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Composition[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Composition | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: CompositionUpdateInput): Promise<Composition> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = CompositionUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, COMPOSITIONS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`composition ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
